import {
  ComponentPropsWithRef,
  ForwardedRef,
  ReactNode,
  forwardRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "flex w-full flex-col gap-10",
    inputRowContainer: "group flex flex-row items-center",
    leadingContainer:
      "rounded-l-sm border border-r-0 border-secondary py-15 pl-20 pr-0 transition-colors group-focus-within:!border-secondary group-placeholder-shown-within:border-gray",
    inputContainer: "relative w-full",
    input:
      "peer w-full rounded-sm border border-secondary bg-white px-20 py-15 placeholder-transparent ring-0 transition-colors placeholder-shown:border-gray focus:border-secondary focus:outline-none group-focus-within:border-secondary",
    label:
      "absolute -top-10 left-10 bg-white px-10 !text-caption transition-all peer-placeholder-shown:left-10 peer-placeholder-shown:top-[23px] peer-focus:-top-10 peer-focus:left-10",
    trailingContainer:
      "rounded-r-sm border border-l-0 border-secondary bg-white py-15 pl-0 pr-20 transition-colors group-focus-within:!border-secondary group-placeholder-shown-within:border-gray",
    helperText: "flex flex-row items-center gap-10 !text-caption",
  },
  variants: {
    variant: {
      error: {
        leadingContainer:
          "border-red text-red group-focus-within:!border-red group-placeholder-shown-within:border-red",
        input: "border-red placeholder-shown:border-red focus:border-red",
        label: "text-red",
        trailingContainer:
          "border-red text-red group-focus-within:!border-red group-placeholder-shown-within:border-red",
        helperText: "text-red",
      },
      warning: {
        leadingContainer:
          "border-orange text-orange group-focus-within:!border-orange group-placeholder-shown-within:border-orange",
        input:
          "border-orange placeholder-shown:border-orange focus:border-orange",
        label: "text-orange",
        trailingContainer:
          "border-orange text-orange group-focus-within:!border-orange group-placeholder-shown-within:border-orange",
        helperText: "text-orange",
      },
      success: {
        leadingContainer:
          "border-green text-green group-focus-within:!border-green group-placeholder-shown-within:border-green",
        input: "border-green placeholder-shown:border-green focus:border-green",
        label: "text-green",
        trailingContainer:
          "border-green text-green group-focus-within:!border-green group-placeholder-shown-within:border-green",
        helperText: "text-green",
      },
    },
    hasLeadingSlot: {
      true: "",
    },
    hasTrailingSlot: {
      true: "",
    },
  },
  compoundSlots: [
    {
      slots: ["input"],
      hasLeadingSlot: true,
      className: "rounded-l-none border-l-0",
    },
    {
      slots: ["input"],
      hasTrailingSlot: true,
      className: "rounded-r-none border-r-0",
    },
  ],
});

type BaseProps = {
  variant?: VariantProps<typeof styles>["variant"];
  label: string;
  name: string;
  helperText?: string | false;
  leadingSlot?: ReactNode;
  trailingSlot?: ReactNode;
  testId?: string;
  helperTextTestId?: string;
};

type InputProps = BaseProps & ComponentPropsWithRef<"input">;

const Input = forwardRef(
  (
    {
      variant,
      label,
      name,
      helperText,
      leadingSlot,
      trailingSlot,
      testId,
      helperTextTestId,
      ...rest
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement | null>,
  ) => {
    const {
      base,
      inputRowContainer,
      leadingContainer,
      inputContainer,
      input,
      label: labelStyles,
      trailingContainer,
      helperText: helperTextStyles,
    } = styles({
      variant,
      hasLeadingSlot: !!leadingSlot,
      hasTrailingSlot: !!trailingSlot,
    });

    const renderLeadingSlot = () =>
      !!leadingSlot && <div className={leadingContainer()}>{leadingSlot}</div>;

    const renderTrailingSlot = () =>
      !!trailingSlot && (
        <div className={trailingContainer()}>{trailingSlot}</div>
      );

    const renderHelperText = () =>
      helperText && (
        <p data-testid={helperTextTestId} className={helperTextStyles()}>
          <FontAwesomeIcon icon={faInfoCircle} />
          {helperText}
        </p>
      );

    return (
      <div className={base()}>
        <div className={inputRowContainer()}>
          {renderLeadingSlot()}
          <div className={inputContainer()}>
            <input
              className={input()}
              placeholder={label}
              name={name}
              id={rest.id || name}
              ref={ref}
              aria-invalid={variant === "error"}
              data-testid={testId}
              {...rest}
            />
            <label className={labelStyles()} htmlFor={rest.id || name}>
              {label}
            </label>
          </div>
          {renderTrailingSlot()}
        </div>
        {renderHelperText()}
      </div>
    );
  },
);

export default Input;
